/**
 * ProductTransactionService
 * Service for ProductTransaction entity operations
 * Route: api/product-transactions
 * Controller: ProductTransactionsController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * ProductTransactionService
 * Handles product transaction CRUD operations and reporting queries
 */
export class ProductTransactionService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ProductTransactionService.DEFAULT_SERVICE_PATH,
            basePath: ProductTransactionService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== FILTERED LIST ====================
    /**
     * Get transactions with optional filters
     * GET /api/product-transactions?productId=&locationId=&typeId=
     * @param productId - Optional product ID filter
     * @param locationId - Optional location ID filter
     * @param typeId - Optional transaction type filter
     * @returns Array of product transactions
     */
    async getAll(productId, locationId, typeId) {
        const response = await this.getAllRaw(productId, locationId, typeId);
        return response.data ?? [];
    }
    /**
     * Get transactions with optional filters (raw)
     * @param productId - Optional product ID filter
     * @param locationId - Optional location ID filter
     * @param typeId - Optional transaction type filter
     * @returns ResponseData with filtered product transactions
     */
    getAllRaw(productId, locationId, typeId) {
        const url = this.buildUrlWithParams(undefined, { productId, locationId, typeId });
        return this.http.get(url);
    }
    // ==================== LOOKUPS ====================
    /**
     * Get transactions for a product
     * GET /api/product-transactions/product/{productId}
     * @param productId - Product ID
     * @returns Array of transactions for the product
     */
    async getByProduct(productId) {
        const response = await this.getByProductRaw(productId);
        return response.data ?? [];
    }
    /**
     * Get transactions for a product (raw)
     * @param productId - Product ID
     * @returns ResponseData with transactions for the product
     */
    getByProductRaw(productId) {
        const endpoint = this.getEndpoint('product', `product/${productId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get transactions for a location
     * GET /api/product-transactions/location/{locationId}
     * @param locationId - Location ID
     * @returns Array of transactions for the location
     */
    async getByLocation(locationId) {
        const response = await this.getByLocationRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get transactions for a location (raw)
     * @param locationId - Location ID
     * @returns ResponseData with transactions for the location
     */
    getByLocationRaw(locationId) {
        const endpoint = this.getEndpoint('location', `location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get transactions within a date range
     * GET /api/product-transactions/date-range?start=&end=
     * @param start - Start date (ISO string or Date)
     * @param end - End date (ISO string or Date)
     * @returns Array of transactions within the date range
     */
    async getByDateRange(start, end) {
        const response = await this.getByDateRangeRaw(start, end);
        return response.data ?? [];
    }
    /**
     * Get transactions within a date range (raw)
     * @param start - Start date (ISO string or Date)
     * @param end - End date (ISO string or Date)
     * @returns ResponseData with transactions in the date range
     */
    getByDateRangeRaw(start, end) {
        const endpoint = this.getEndpoint('date-range', 'date-range');
        const url = this.buildUrlWithParams(endpoint, {
            start: this.formatDateParam(start),
            end: this.formatDateParam(end),
        });
        return this.http.get(url);
    }
    /**
     * Normalize date param to ISO string
     */
    formatDateParam(value) {
        return value instanceof Date ? value.toISOString() : value;
    }
}
ProductTransactionService.DEFAULT_SERVICE_PATH = 'products';
ProductTransactionService.BASE_PATH = 'api/product-transactions';
//# sourceMappingURL=ProductTransactionService.js.map